import {
  cloneElement,
  isValidElement,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

type TooltipSide = "top" | "right" | "bottom" | "left" | "bottom-right";
type TooltipVariant = "default" | "muted";

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: TooltipSide;
  disabled?: boolean;
  followCursor?: boolean;
  variant?: TooltipVariant;
  className?: string;
  /**
   * Caps tooltip height and scrolls overflow — use for long lists (e.g. many
   * labels) so the bubble stays within the viewport.
   */
  scrollMaxHeight?: string | number;
  /**
   * Only reveal on hover — never on keyboard/click focus, and hide immediately
   * if the trigger becomes focused. Use for inputs/dropdowns where a focus
   * tooltip would obscure the field's own popover.
   */
  hoverOnly?: boolean;
}

function isTooltipContentEmpty(content: ReactNode): boolean {
  if (content == null || typeof content === "boolean") return true;
  if (typeof content === "string") return !content.trim();
  if (typeof content === "number") return false;
  if (Array.isArray(content)) return content.every(isTooltipContentEmpty);
  return false;
}

function tooltipScrollStyle(maxHeight: string | number): CSSProperties {
  return {
    maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
  };
}

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (value: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") ref(value);
      else (ref as React.MutableRefObject<T | null>).current = value;
    }
  };
}

/** Footprint of the tail along the plate edge, and how far it protrudes. */
const ARROW_WIDTH = 14;
const ARROW_DEPTH = 7;
/** Protrusion plus a little breathing space between plate and trigger. */
const SIDE_GAP = ARROW_DEPTH + 3;
/**
 * Distance from the bubble's left edge to the arrow's centre, for
 * `bottom-right`. Kept clear of the rounded corner so the tail joins a
 * straight run of the plate edge.
 */
const ARROW_INSET = 26;

/**
 * Neutral dark-grey metal — no blue in it, so it reads as machined steel rather
 * than a tinted panel. Bright top bevel, a narrow specular band across the
 * middle, then a fall-off to a near-black base, over a fine vertical grain.
 *
 * The layers themselves live in `theme/global.css` (`--metal-*`) because the
 * scrollbar shares them, and a `::-webkit-scrollbar-thumb` fill can only be set
 * from a stylesheet.
 */
const METAL_FILL = "var(--metal-fill)";
/** Fill at the very top, the middle and the very bottom of the base gradient. */
const METAL_TOP = "#5C5C60";
const METAL_MID = "#46464A";
const METAL_BOTTOM = "#1B1B1E";
/** Rim light along the lit (top) edge of the plate. */
const METAL_EDGE_LIGHT = "rgba(255,255,255,0.26)";
/**
 * Bevel — inset highlight along the lit edge, inset shadow along the shadowed
 * ones — plus a tight contact shadow and a wider ambient one. Together they
 * make the plate read as a raised solid rather than a flat rectangle.
 */
const METAL_SHADOW = [
  `inset 0 1px 0 ${METAL_EDGE_LIGHT}`,
  "inset 1px 0 0 rgba(255,255,255,0.08)",
  "inset -1px 0 0 rgba(0,0,0,0.35)",
  "inset 0 -1px 0 rgba(0,0,0,0.55)",
  "0 1px 2px rgba(0,0,0,0.45)",
  "0 12px 26px -10px rgba(0,0,0,0.6)",
].join(", ");

/**
 * The tail is a clipped triangle sitting wholly outside the plate edge (bar a
 * 1px overlap that hides the seam) rather than a rotated square, so it never
 * paints a corner back over the gradient. Its fill matches the part of the
 * gradient it grows out of, so the two read as one piece of metal.
 */
function arrowStyleForSide(side: TooltipSide): React.CSSProperties {
  const flush = -(ARROW_DEPTH - 1);
  const vertical = { width: ARROW_WIDTH, height: ARROW_DEPTH } as const;
  const horizontal = { width: ARROW_DEPTH, height: ARROW_WIDTH } as const;
  // The tail catches the same light as the edge it leaves, so an upward one is
  // lit along its slopes while a downward one only ever shows its shadow side.
  const litTail = `linear-gradient(180deg, ${METAL_EDGE_LIGHT} 0 1px, #6C6C72 1px, ${METAL_TOP} 100%)`;

  switch (side) {
    case "right":
      return {
        ...horizontal,
        left: flush,
        top: "50%",
        marginTop: -ARROW_WIDTH / 2,
        clipPath: "polygon(0 50%, 100% 0, 100% 100%)",
        background: `linear-gradient(90deg, #525256, ${METAL_MID})`,
      };
    case "left":
      return {
        ...horizontal,
        right: flush,
        top: "50%",
        marginTop: -ARROW_WIDTH / 2,
        clipPath: "polygon(100% 50%, 0 0, 0 100%)",
        background: `linear-gradient(270deg, #2E2E31, ${METAL_MID})`,
      };
    case "top":
      // Hangs off the bottom of the plate, where the gradient is darkest.
      return {
        ...vertical,
        bottom: flush,
        left: "50%",
        marginLeft: -ARROW_WIDTH / 2,
        clipPath: "polygon(0 0, 100% 0, 50% 100%)",
        background: `linear-gradient(180deg, ${METAL_BOTTOM}, #121214)`,
      };
    case "bottom-right":
      // Bubble hangs to the right of the trigger, so the arrow stays near its
      // left edge; positionForSide shifts the bubble by the same inset so the
      // arrow still lands on the trigger's centre.
      return {
        ...vertical,
        top: flush,
        left: ARROW_INSET - ARROW_WIDTH / 2,
        clipPath: "polygon(50% 0, 100% 100%, 0 100%)",
        background: litTail,
      };
    case "bottom":
    default:
      return {
        ...vertical,
        top: flush,
        left: "50%",
        marginLeft: -ARROW_WIDTH / 2,
        clipPath: "polygon(50% 0, 100% 100%, 0 100%)",
        background: litTail,
      };
  }
}

function positionForSide(rect: DOMRect, side: TooltipSide): { top: number; left: number; transform: string } {
  const gap = SIDE_GAP;
  switch (side) {
    case "right":
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + gap,
        transform: "translateY(-50%)",
      };
    case "left":
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - gap,
        transform: "translate(-100%, -50%)",
      };
    case "top":
      return {
        top: rect.top - gap,
        left: rect.left + rect.width / 2,
        transform: "translate(-50%, -100%)",
      };
    case "bottom-right":
      // Hangs below and to the right of the trigger, so it clears the
      // left-aligned content directly beneath it (e.g. the next table row).
      return {
        top: rect.bottom + gap,
        left: rect.left + rect.width / 2 - ARROW_INSET,
        transform: "none",
      };
    case "bottom":
    default:
      return {
        top: rect.bottom + gap,
        left: rect.left + rect.width / 2,
        transform: "translateX(-50%)",
      };
  }
}

function positionForCursor(clientX: number, clientY: number): {
  top: number;
  left: number;
  transform: string;
} {
  const gap = 12;
  return {
    top: clientY + gap,
    left: clientX + gap,
    transform: "none",
  };
}

/** Breathing space kept between the bubble and the edge of the viewport. */
const VIEWPORT_MARGIN = 8;

/**
 * Sides to try, best first, for each preferred side. A tooltip pinned to the
 * right edge of the screen has to open leftwards or it is cropped — the
 * fallbacks run along the same axis first, so a flipped bubble still points at
 * the trigger the way the caller intended.
 */
const SIDE_FALLBACKS: Record<TooltipSide, TooltipSide[]> = {
  right: ["right", "left", "bottom", "top"],
  left: ["left", "right", "bottom", "top"],
  top: ["top", "bottom", "right", "left"],
  bottom: ["bottom", "top", "right", "left"],
  "bottom-right": ["bottom-right", "bottom", "top", "left"],
};

/**
 * Where the bubble's box would land on `side`, in viewport coordinates.
 *
 * `positionForSide` returns an anchor point plus a CSS transform; these are the
 * same transforms restated as multiples of the bubble's own size, so the box
 * can be measured before the browser paints it anywhere.
 */
const TRANSFORM_OFFSET: Record<TooltipSide, { x: number; y: number }> = {
  right: { x: 0, y: -0.5 },
  left: { x: -1, y: -0.5 },
  top: { x: -0.5, y: -1 },
  bottom: { x: -0.5, y: 0 },
  "bottom-right": { x: 0, y: 0 },
};

function boxForSide(
  rect: DOMRect,
  side: TooltipSide,
  width: number,
  height: number,
): { top: number; left: number; right: number; bottom: number } {
  const { top, left } = positionForSide(rect, side);
  const offset = TRANSFORM_OFFSET[side];
  const x = left + offset.x * width;
  const y = top + offset.y * height;
  return { top: y, left: x, right: x + width, bottom: y + height };
}

/** First side whose box fits the viewport, or the preferred one if none does. */
function fittingSide(rect: DOMRect, preferred: TooltipSide, width: number, height: number): TooltipSide {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  for (const side of SIDE_FALLBACKS[preferred]) {
    const box = boxForSide(rect, side, width, height);
    if (
      box.left >= VIEWPORT_MARGIN &&
      box.right <= vw - VIEWPORT_MARGIN &&
      box.top >= VIEWPORT_MARGIN &&
      box.bottom <= vh - VIEWPORT_MARGIN
    ) {
      return side;
    }
  }
  return preferred;
}

/**
 * Portal tooltip so labels are not clipped by sidebar overflow.
 */
export function Tooltip({
  content,
  children,
  side = "right",
  disabled = false,
  followCursor = false,
  variant = "default",
  className,
  scrollMaxHeight,
  hoverOnly = false,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<{ top: number; left: number; transform: string }>({
    top: 0,
    left: 0,
    transform: "translateY(-50%)",
  });
  /** Side the bubble actually opened on — `side` unless it would be cropped. */
  const [placedSide, setPlacedSide] = useState<TooltipSide>(side);
  const triggerRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const focusedRef = useRef(false);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    setPlacedSide(side);
    setStyle(positionForSide(el.getBoundingClientRect(), side));
  }, [side]);

  /**
   * Re-place the bubble once its real size is known. The preferred side is a
   * hint: a trigger near the right edge of the screen would open a bubble that
   * runs off it, so the same content is flipped to whichever side fits and the
   * tail is redrawn to match.
   */
  useLayoutEffect(() => {
    if (!open || followCursor) return;
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;
    const { width, height } = bubble.getBoundingClientRect();
    const chosen = fittingSide(trigger.getBoundingClientRect(), side, width, height);
    if (chosen === placedSide) return;
    setPlacedSide(chosen);
    setStyle(positionForSide(trigger.getBoundingClientRect(), chosen));
  }, [open, side, followCursor, content, placedSide]);

  const updateCursorPosition = useCallback((clientX: number, clientY: number) => {
    setStyle(positionForCursor(clientX, clientY));
  }, []);

  const scheduleShow = useCallback(() => {
    // While the trigger is focused (e.g. an open dropdown), the tooltip would
    // obscure the field's own popover, so hover must not reveal it.
    if (hoverOnly && focusedRef.current) return;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    showTimerRef.current = setTimeout(() => {
      if (!followCursor) updatePosition();
      setOpen(true);
    }, 250);
  }, [followCursor, updatePosition, hoverOnly]);

  const scheduleHide = useCallback(() => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    // Long enough for the pointer to cross the gap between trigger and bubble,
    // so the user can move onto the tooltip to read or select its text.
    hideTimerRef.current = setTimeout(() => setOpen(false), 200);
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  if (disabled || isTooltipContentEmpty(content)) {
    return <>{children}</>;
  }

  const tooltipBody = scrollMaxHeight ? (
    <div
      className="scrollbar-metal -mx-1 min-w-[120px] overflow-y-auto overscroll-contain pr-1 [scrollbar-gutter:stable]"
      style={tooltipScrollStyle(scrollMaxHeight)}
    >
      {typeof content === "string" ? (
        <span className="block whitespace-pre-line">{content}</span>
      ) : (
        content
      )}
    </div>
  ) : (
    content
  );

  const handlers = {
    onMouseEnter: (e: React.MouseEvent) => {
      if (followCursor) updateCursorPosition(e.clientX, e.clientY);
      scheduleShow();
    },
    onMouseMove: (e: React.MouseEvent) => {
      if (followCursor) updateCursorPosition(e.clientX, e.clientY);
    },
    onMouseLeave: scheduleHide,
    onFocus: () => {
      if (hoverOnly) {
        // Clicking/tabbing into the field must dismiss the tooltip, not show it.
        focusedRef.current = true;
        if (showTimerRef.current) clearTimeout(showTimerRef.current);
        setOpen(false);
        return;
      }
      scheduleShow();
    },
    onBlur: () => {
      focusedRef.current = false;
      scheduleHide();
    },
  };

  const trigger = isValidElement(children)
    ? (() => {
        const child = children as ReactElement<{ className?: string; ref?: Ref<HTMLElement> }>;
        return cloneElement(child, {
          ...handlers,
          ref: mergeRefs(child.props.ref, triggerRef),
          className: cn(child.props.className, className),
        });
      })()
    : (
      <span
        ref={triggerRef as Ref<HTMLSpanElement>}
        className={cn("inline-flex", className)}
        {...handlers}
      >
        {children}
      </span>
    );

  // The arrow only makes sense when the bubble is anchored to the trigger;
  // a cursor-following bubble has no fixed edge to point from.
  const arrow = followCursor ? null : arrowStyleForSide(placedSide);

  const tooltip =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={bubbleRef}
            role="tooltip"
            className={cn(
              "fixed z-[9999] px-4 py-2.5 text-[13px] font-medium leading-snug",
              // A cursor-following bubble can never be hovered — it moves with
              // the pointer — so it must not eat events. Anchored ones stay
              // interactive so their text can be read and selected.
              followCursor ? "pointer-events-none" : "pointer-events-auto select-text cursor-text",
              // Machined metal plate: gradient fill, bevelled edges and an
              // engraved label (see METAL_* — set on `style`, since the values
              // are shared with the tail).
              "text-zinc-50 [text-shadow:0_1px_1px_rgba(0,0,0,0.6)]",
              // w-max keeps short labels compact; the max-width is wide enough
              // that a sentence or two reads as a paragraph, not a column.
              // pre-line so string callers can put each item on its own line.
              typeof content === "string" && !scrollMaxHeight
                ? "w-max max-w-[340px] whitespace-pre-line break-words [overflow-wrap:anywhere]"
                : "w-max max-w-[280px] break-words [overflow-wrap:anywhere]",
              // Theme radii are large (sm = 10px, xs = 6px); anything bigger
              // makes a one-line bubble read as a pill.
              variant === "muted" ? "rounded-xs" : "rounded-sm",
            )}
            style={{
              top: style.top,
              left: style.left,
              transform: style.transform,
              background: METAL_FILL,
              boxShadow: METAL_SHADOW,
            }}
            onMouseEnter={followCursor ? undefined : cancelHide}
            onMouseLeave={followCursor ? undefined : scheduleHide}
            // The bubble is portalled to <body>, but React propagates synthetic
            // events along the component tree rather than the DOM one — so
            // without this, clicking a tooltip fires the onClick of whatever
            // owns it (e.g. opening the table row sitting behind the bubble).
            // Propagation only: the default action still runs, so the text
            // inside stays selectable.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {arrow && (
              <span
                aria-hidden="true"
                className="absolute [filter:drop-shadow(0_1px_1px_rgba(0,0,0,0.4))]"
                style={arrow}
              />
            )}
            {tooltipBody}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {trigger}
      {tooltip}
    </>
  );
}
